import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  ThermometerSun, 
  X, 
  Sparkles, 
  Binary 
} from 'lucide-react'; 
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { useSalesEngine } from '../hooks/useSalesEngine';

/**
 * BLOG: SMART LIGHTING LOGIC
 * Positions the firm as a consultant for high-end technical infrastructure.
 * Replaces 'service' language with 'architecture' and 'protocol' language.
 */
const BlogSmartLighting = ({ sessionID }) => {
  const [isContactOpen, setIsContactOpen] = useState(false);
  
  const BACKEND_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:5000'
    : 'https://ava.medconsulting.solutions';

  const { 
    logPageChange, 
    logSpecClick, 
    isModalOpen: isNudgeOpen, 
    setIsModalOpen: setIsNudgeOpen, 
    nudgeMessage 
  } = useSalesEngine(BACKEND_URL, sessionID);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    window.scrollTo(0, 0);
    logPageChange(); 
  }, [logPageChange]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.3 } }
  };

  const itemVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
  };

  return (
    <div className="bg-white min-h-screen selection:bg-amber-100 overflow-x-hidden">
      <SEO title="Smart Home Systems Design 2026 | Micheal's Technical Design" path="/blogsmartlighting" />

      {/* Progress Bar */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-amber-600 origin-left z-[120]" style={{ scaleX }} />
      
      {/* Sales Engine Nudge: Focused on "Design Analysis" */}
      <AnimatePresence>
        {isNudgeOpen && (
          <motion.div 
            initial={{ opacity: 0, x: 50 }} 
            animate={{ opacity: 1, x: 0 }} 
            exit={{ opacity: 0, x: 20 }} 
            className="fixed bottom-6 right-6 z-[130] w-80 bg-stone-900 text-white p-6 shadow-2xl border-l-4 border-amber-600"
          >
            <button onClick={() => setIsNudgeOpen(false)} className="absolute top-2 right-2 text-stone-500 hover:text-white"><X size={16} /></button>
            <div className="flex items-center gap-2 text-amber-500 uppercase text-[10px] tracking-[0.2em] font-bold mb-3"><Sparkles size={12} /> Design Analysis</div>
            <p className="text-sm font-light leading-relaxed text-stone-300 mb-6">{nudgeMessage || "Request the technical blueprints for this configuration."}</p>
            <button 
              onClick={() => { setIsContactOpen(true); setIsNudgeOpen(false); }} 
              className="w-full bg-amber-600 py-3 text-[10px] font-bold uppercase tracking-widest transition-colors hover:bg-amber-500"
            >
              Request Design Audit
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <header className="py-24 px-6 bg-stone-50 border-b border-stone-200">
        <div className="max-w-3xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-700 font-bold text-xs uppercase tracking-[0.3em] mb-8 hover:text-amber-600 transition-colors"><ArrowLeft size={14} /> Portfolio</Link>
          <h1 className="text-4xl md:text-6xl font-serif text-stone-900 leading-tight mb-6">
            Automated Lighting Logic <br />
            <span className="italic font-light text-stone-500 text-3xl md:text-5xl">The 2026 Infrastructure Standard</span>
          </h1>
          <p className="text-stone-400 text-[10px] uppercase tracking-[0.5em] font-bold">Technical Directive // Michael's Design Consultation Firm</p>
        </div>
      </header>

      <article className="py-20 px-6 max-w-5xl mx-auto">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} className="space-y-32">
          
          {/* Trend 1: Circadian Design */}
          <motion.section variants={itemVariants} className="group" onMouseEnter={() => logSpecClick("READING_CIRCADIAN")}>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-amber-50 rounded-full">
                    <ThermometerSun className="text-amber-600" size={24} />
                  </div>
                  <h2 className="text-3xl font-serif text-stone-900 italic">I. Circadian Logic Mapping</h2>
                </div>
                <p className="text-stone-600 leading-relaxed font-light text-lg">
                  We engineer lighting protocols that automate spectral shifts. This is a logic-driven approach to wellness, syncing architectural hardware with human biology through adaptive color temperature (Kelvin) control.
                </p>
              </div>
              <div className="relative p-10 bg-stone-50 border border-stone-100 rounded-sm shadow-inner text-center">
                <span className="text-[10px] uppercase tracking-widest text-stone-400 font-black">Spectral Protocol Analysis</span>
                
                <div className="w-full h-1.5 bg-gradient-to-r from-amber-200 via-stone-100 to-blue-300 rounded-full mt-6" />
                <div className="flex justify-between text-[9px] mt-3 font-mono text-stone-400 uppercase tracking-tighter">
                  <span>Dawn // 2700K</span>
                  <span>Solar Peak // 5500K</span>
                  <span>Dusk // 1800K</span>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Trend 2: PoE Design */}
          <motion.section variants={itemVariants} onMouseEnter={() => logSpecClick("READING_LOW_VOLTAGE")}>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-1 md:order-2">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-stone-100 rounded-full">
                    <Binary className="text-stone-600" size={24} />
                  </div>
                  <h2 className="text-3xl font-serif text-stone-900 italic">II. PoE Digital Backbone</h2>
                </div>
                <p className="text-stone-600 leading-relaxed font-light text-lg mb-6">
                  By shifting to Power-over-Ethernet (PoE) architecture, we centralize control logic. This allows for granular system-level diagnostics and future-proofing via managed software architecture.
                </p>
                <div className="p-4 bg-stone-900 rounded-sm text-amber-500 font-mono text-[9px] uppercase tracking-widest border-l-2 border-amber-600">
                  Spec: IEEE 802.3bt // Managed Node Architecture
                </div>
              </div>
              
              <div className="bg-stone-50 aspect-square md:aspect-video rounded-sm border border-stone-200 flex flex-col items-center justify-center p-8 relative overflow-hidden group">
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none group-hover:opacity-[0.05] transition-opacity">
                  <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                </div>
                
                <div className="mt-4 text-[10px] font-mono text-stone-400 uppercase tracking-[0.2em] text-center">
                  Infrastructure Mapping<br/>
                  <span className="text-stone-300 font-light italic">Consultation Node 0x7F</span>
                </div>
              </div>
            </div>
          </motion.section>

          {/* CTA Section - Pivoted to Consultation */}
          <motion.div 
            whileHover={{ y: -5 }} 
            className="mt-40 p-16 bg-stone-950 text-white text-center shadow-2xl relative overflow-hidden rounded-sm"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-amber-600"></div>
            <h3 className="text-4xl font-serif mb-6 italic">"Design is the invisible logic."</h3>
            <p className="text-stone-400 mb-12 font-light max-w-xl mx-auto text-lg leading-relaxed">
              Shift your project from basic implementation to architectural systems management. Consult with our technical design team for a comprehensive infrastructure roadmap.
            </p>
            <button 
              onClick={() => setIsContactOpen(true)} 
              className="bg-amber-600 text-white px-12 py-5 font-bold text-[10px] uppercase tracking-[0.4em] hover:bg-amber-500 transition-all shadow-xl"
            >
              Request Technical Design Proposal
            </button>
          </motion.div>
        </motion.div>
      </article>

      {/* Contact Modal */}
      <AnimatePresence>
        {isContactOpen && (
          <div className="fixed inset-0 z-[140] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsContactOpen(false)} className="absolute inset-0 bg-stone-900/80 backdrop-blur-md" />
            <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-sm relative z-10 p-8 shadow-2xl">
                <button onClick={() => setIsContactOpen(false)} className="absolute top-6 right-6 text-stone-400 hover:text-stone-900 transition-colors"><X size={24} /></button>
                <div className="mb-12 border-b border-stone-100 pb-8">
                  <p className="text-[10px] uppercase tracking-[0.5em] text-amber-700 font-black mb-2">Phase 01</p>
                  <h2 className="text-3xl font-serif italic text-stone-900">Initiate Diagnostic Audit</h2>
                </div>
                <ContactForm sessionId={sessionID} />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BlogSmartLighting;